import type { Reason } from '@amzn/gbsa-helios-typescript-client';
import type { NaReasonOverride } from './preserveResponseFields';

/**
 * Selected-value shape for {@link NaReasonSelect}. Mirrors the
 * component's `NaReasonSelectValue` but lives here so the
 * composition path stays pure — the component itself needn't
 * know about drafts or persisted state.
 */
export interface NaReasonSelectValue {
  readonly reasonId: string;
  readonly label: string;
}

/**
 * Compose the effective Select value from a target's draft
 * override, its persisted label, and the loaded reasons list.
 *
 * Priority (matches the desktop picker's fallback order):
 *
 *   1. Draft is `null` → CLEAR intent. Return null so the Select
 *      renders the placeholder even if a stale label sits on the
 *      server.
 *   2. Draft is `{reasonId, label}` → auditor's live pick. Return
 *      verbatim; the id is guaranteed well-formed because the
 *      picker only emits values sourced from `reasons`.
 *   3. Draft is absent AND server carries a label → recover the
 *      id by matching label against `reasons`. When the label
 *      matches an active reason, return the recovered pair; when
 *      no match exists (deactivated / cross-division / lost id),
 *      return `{ reasonId: '', label }` so the Select's synthetic-
 *      option branch keeps the label visible.
 *   4. Nothing anywhere → null.
 *
 * Pure — no side effects, no react. Extracted from the
 * interaction render so the JSX stays short and the composition
 * gets tested in isolation.
 */
export function resolveNaReasonSelectValue(
  draftSlot: NaReasonOverride | undefined,
  persistedLabel: string | undefined,
  reasons: readonly Reason[],
): NaReasonSelectValue | null {
  if (draftSlot === null) return null;
  if (draftSlot !== undefined) return draftSlot;
  if (!persistedLabel) return null;
  const matched = reasons.find((r) => r.label === persistedLabel);
  if (matched) {
    return { reasonId: matched.reasonId, label: matched.label };
  }
  return { reasonId: '', label: persistedLabel };
}

/**
 * "Does this target have a reason selected right now?" predicate.
 * Composed from the same inputs as
 * {@link resolveNaReasonSelectValue} but returns a plain boolean
 * for use in visibility rules that don't care WHICH reason is
 * selected, only THAT one is.
 *
 * Rules:
 *   - Draft is `null` (cleared) → false
 *   - Draft is set → true
 *   - Draft is absent AND persisted label is non-empty → true
 *   - Otherwise → false
 */
export function hasNaReasonForTarget(
  draftSlot: NaReasonOverride | undefined,
  persistedLabel: string | undefined,
): boolean {
  if (draftSlot === null) return false;
  if (draftSlot !== undefined) return true;
  return !!persistedLabel;
}
